import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import Checklist from '@editorjs/checklist';
import Quote from '@editorjs/quote';
import Code from '@editorjs/code';
import ImageTool from '@editorjs/image';

const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:5000' : '';

const EditorComponent = forwardRef(({ initialData, onReady }, ref) => {
  const editorRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const holderId = useRef(`editorjs-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  
  // 确保holderId只生成一次
  useEffect(() => {
    if (!holderId.current) {
      holderId.current = `editorjs-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }
  }, []);

  useEffect(() => {
    const holderEl = document.getElementById(holderId.current);
    if (!holderEl) {
      console.error('Editor holder not found');
      return;
    }

    // 检查是否已经有编辑器实例
    if (editorRef.current) {
      // 如果已经有实例且就绪，更新数据
      if (initialData && isReady) {
        try {
          const parsedData = typeof initialData === 'string' ? JSON.parse(initialData) : initialData;
          if (editorRef.current.blocks && typeof editorRef.current.blocks.render === 'function') {
            editorRef.current.blocks.render(parsedData);
          }
        } catch (e) {
          console.error('更新编辑器数据失败:', e);
        }
      }
      return;
    }

    const editor = new EditorJS({
      holder: holderId.current,
      minHeight: 150,
      placeholder: '请输入内容...',
      data: initialData || {},
      tools: {
        header: {
          class: Header,
          config: { placeholder: '输入标题...', levels: [1, 2, 3, 4], defaultLevel: 2 },
          inlineToolbar: true
        },
        list: { class: List, inlineToolbar: true },
        checklist: { class: Checklist, inlineToolbar: true },
        quote: { class: Quote, inlineToolbar: true },
        code: { class: Code },
        image: {
          class: ImageTool,
          config: {
            uploader: {
              uploadByFile: async (file) => {
                const formData = new FormData();
                formData.append('image', file);
                const response = await fetch(`${API_BASE_URL}/api/contents/upload`, {
                  method: 'POST',
                  body: formData
                });
                const result = await response.json();
                if (result.success === 1 && result.file) {
                  return {
                    success: 1,
                    file: { url: API_BASE_URL + result.file.url }
                  };
                }
                return { success: 0, message: result.message || '上传失败' };
              }
            }
          }
        }
      },
      onReady: () => {
        console.log('Editor.js is ready to work!');
        setIsReady(true);
        if (onReady && typeof onReady === 'function') {
          onReady();
        }
      },
      onChange: (api, event) => {
        console.log('Content changed:', event);
      }
    });

    editorRef.current = editor;

    return () => {
      setIsReady(false);
      if (editorRef.current && typeof editorRef.current.destroy === 'function') {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
  }, [initialData, onReady, isReady]);

  // 使用useImperativeHandle设置ref
  useImperativeHandle(ref, () => ({
    getEditorData: async () => {
      if (!editorRef.current || !isReady) {
        console.log('Editor not ready, isReady:', isReady);
        return null;
      }
      try {
        const outputData = await editorRef.current.save();
        return JSON.stringify(outputData);
      } catch (error) {
        console.log('Saving failed: ', error);
        return null;
      }
    },
    setEditorData: async (data) => {
      if (editorRef.current && data && isReady) {
        try {
          const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
          if (editorRef.current.blocks && typeof editorRef.current.blocks.render === 'function') {
            await editorRef.current.blocks.render(parsedData);
          }
        } catch (e) {
          console.error('设置编辑器数据失败:', e);
        }
      }
    },
    isReady: () => isReady,
    getEditorInstance: () => editorRef.current
  }), [isReady]);

  return (
    <div 
      id={holderId.current}
      className="editor-container"
      style={{ minHeight: 150, border: '1px solid #d9d9d9', borderRadius: 4 }}
    ></div>
  );
});

export default EditorComponent;